import React, { useMemo, useState } from 'react';
import { Badge, Box, Flex, Input, InputGroup, Select, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { IPagination } from '../../../types/shared';
import { DataTable } from '../../../components/datatable';
import { ReturnButton } from '../../../components/buttons/ReturnButton';
import { GlassCard, GlassPageBg } from '../../../components/glass';
import {
  ISocioUnico,
  ISociosUnicosPage,
  TipoPessoaSocio,
  useSocietario,
} from '../../../hooks/useSocietario';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

const tipoPessoaLabel: Record<TipoPessoaSocio, string> = {
  pf: 'Pessoa Física',
  pj: 'Pessoa Jurídica',
};

export const SocietarioSociosPage = () => {
  const { getSociosUnicos } = useSocietario();
  const [filters, setFilters] = useSearchParams(createSearchParams({ page: '1', size: '20' }));
  // search local — debounce simples no submit (Enter / blur).
  const [searchInput, setSearchInput] = useState(() => filters.get('search') ?? '');

  const queryFilters = useMemo(() => {
    const params: Record<string, any> = {
      page: Number(filters.get('page') ?? '1'),
      size: Number(filters.get('size') ?? '20'),
    };
    const tipoPessoa = filters.get('tipo_pessoa');
    if (tipoPessoa) params.tipo_pessoa = tipoPessoa;
    const search = filters.get('search');
    if (search) params.search = search;
    return params;
  }, [filters]);

  const { data, isFetching } = useQuery<ISociosUnicosPage>({
    queryKey: ['societario-socios-unicos', queryFilters],
    queryFn: getSociosUnicos,
  });

  const items: ISocioUnico[] = data?.items ?? [];
  const pagination: IPagination = {
    page: data?.page ?? 1,
    pages: data?.pages ?? 1,
    size: data?.size ?? 20,
    total: data?.total ?? 0,
    next_page: data?.next_page ?? null,
    previous_page: data?.previous_page ?? null,
  };

  const updateFilter = (key: string, value: string) => {
    setFilters(state => {
      if (value) state.set(key, value);
      else state.delete(key);
      state.set('page', '1');
      return state;
    });
  };

  const columns: ColumnDef<ISocioUnico>[] = [
    {
      accessorKey: 'nome',
      header: 'Nome / Razão social',
      cell: ({ row }) => (
        <Box textAlign="left">
          <Text fontWeight="medium" noOfLines={1}>
            {row.original.nome}
          </Text>
          {row.original.profissao ? (
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {row.original.profissao}
            </Text>
          ) : null}
        </Box>
      ),
    },
    {
      accessorKey: 'tipo_pessoa',
      header: 'Tipo',
      cell: ({ row }) => (
        <Badge colorScheme={row.original.tipo_pessoa === 'pf' ? 'blue' : 'purple'} fontSize="2xs">
          {tipoPessoaLabel[row.original.tipo_pessoa]}
        </Badge>
      ),
    },
    {
      id: 'documento',
      header: 'CPF / CNPJ',
      cell: ({ row }) => {
        const doc = row.original.cpf || row.original.cnpj;
        if (!doc) return <Text color="gray.400">—</Text>;
        return (
          <Text fontSize="sm" fontFamily="mono">
            {doc}
          </Text>
        );
      },
    },
    {
      id: 'contato',
      header: 'Contato',
      cell: ({ row }) => {
        const { email, telefone } = row.original;
        if (!email && !telefone) return <Text color="gray.400">—</Text>;
        return (
          <Box textAlign="left" fontSize="xs">
            {email ? <Text noOfLines={1}>{email}</Text> : null}
            {telefone ? (
              <Text color="gray.500" noOfLines={1}>
                {telefone}
              </Text>
            ) : null}
          </Box>
        );
      },
    },
    {
      id: 'representante',
      header: 'Representante (PJ)',
      cell: ({ row }) => {
        if (row.original.tipo_pessoa !== 'pj') return <Text color="gray.300">—</Text>;
        const nome = row.original.representante_nome;
        if (!nome) return <Text color="gray.400">—</Text>;
        return (
          <Box textAlign="left" fontSize="xs">
            <Text noOfLines={1}>{nome}</Text>
            {row.original.representante_cpf ? (
              <Text color="gray.500" fontFamily="mono">
                {row.original.representante_cpf}
              </Text>
            ) : null}
          </Box>
        );
      },
    },
    {
      accessorKey: 'contratos_count',
      header: 'Contratos',
      cell: ({ row }) => {
        const count = row.original.contratos_count;
        return (
          <Badge colorScheme={count > 1 ? 'green' : 'gray'} variant="subtle">
            {count}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'last_used',
      header: 'Último uso',
      cell: ({ row }) => <Text fontSize="sm">{formatDate(row.original.last_used)}</Text>,
    },
  ];

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" px={{ base: 4, md: 6 }} py={6}>
        <ReturnButton previousPage="societario" label="Voltar" />
        <PageHeader
          title="Sócios / Cadastros"
          subtitle="Base reutilizável de sócios deduplicados por CPF/CNPJ + nome. Snapshot mais recente."
        />

        <GlassCard intensity="strong" p={5} mt={4}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={3}
            mb={4}
          >
            <Flex gap={3} direction={{ base: 'column', sm: 'row' }} flex={1}>
              <Select
                placeholder="Todos os tipos"
                value={filters.get('tipo_pessoa') ?? ''}
                onChange={e => updateFilter('tipo_pessoa', e.target.value)}
                maxW={{ sm: '220px' }}
                bg="white"
              >
                <option value="pf">Pessoa Física</option>
                <option value="pj">Pessoa Jurídica</option>
              </Select>

              <InputGroup maxW={{ sm: '320px' }}>
                <Input
                  placeholder="Buscar por nome, CPF, CNPJ ou email…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') updateFilter('search', searchInput.trim());
                  }}
                  onBlur={() => updateFilter('search', searchInput.trim())}
                  bg="white"
                />
              </InputGroup>
            </Flex>
          </Flex>

          <DataTable<ISocioUnico>
            columns={columns}
            data={items}
            pagination={pagination}
            emptyMsg="Nenhum sócio encontrado."
            loadingMsg="Carregando sócios..."
            isFetching={isFetching}
          />
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};

export default SocietarioSociosPage;
